import { Box, Group, Stack } from '@mantine/core';
import { useSuspenseApiPublicEnv } from '@repo/api-codegen';
import { Icon, Text } from '@repo/foundations';
import { isEmpty } from 'lodash-es';
import { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router';
import GoogleIcon from './AuthIcons/IconGoogle';
import MicrosoftIcon from './AuthIcons/IconMicrosoft';
import { AuthButton } from './AuthLayouts/AuthButton';
import { AuthStepStartContext } from './AuthStepStartContext';
import type { SsoTypes } from './utils';
import { getSsoName } from './utils';

const REGEX_DOMAIN = /^[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;

interface AuthStepSsoComponent {
	supportedMethods?: (SsoTypes | 'email')[];
	buttonPrefix?: string;
}

export function AuthStepSsoComponent({
	supportedMethods,
	buttonPrefix = 'Continue',
}: AuthStepSsoComponent) {
	const context = useContext(AuthStepStartContext);
	if (!context) {
		throw new Error(
			'AuthStepSsoComponent must be used within a AuthStepStartContextProvider'
		);
	}
	const { forceSSO, email, startSamlAuth } = context;
	const navigate = useNavigate();
	const { data: publicEnv } = useSuspenseApiPublicEnv({});

	const handleGoogle = useCallback(() => {
		const { origin } = window.location;
		const authUrl = `https://auth.secoda.co/auth/google?origin=${encodeURIComponent(origin)}&region=${encodeURIComponent(publicEnv?.REGION || '')}`;
		window.location.href = authUrl;
	}, [publicEnv?.REGION]);

	const handleMicrosoft = useCallback(() => {
		const { origin } = window.location;
		const authUrl = `https://auth.secoda.co/auth/microsoft?origin=${encodeURIComponent(origin)}&region=${encodeURIComponent(publicEnv?.REGION || '')}`;
		window.location.href = authUrl;
	}, [publicEnv?.REGION]);

	const handleSaml = () => {
		if (forceSSO && email.length > 0) {
			// If it's an email address, extract the domain.
			let domain = email.includes('@') ? email.split('@')[1] : email;
			domain = domain.trim().toLowerCase();

			if (domain && !isEmpty(domain) && domain.match(REGEX_DOMAIN)) {
				startSamlAuth(domain);
			}
		} else {
			navigate('/auth/login-sso');
		}
	};

	return (
		<Stack spacing="xs">
			{forceSSO && (
				<Text size="xs" align="center">
					Your Administrator requires that you log in using{' '}
					{getSsoName(forceSSO)}.
				</Text>
			)}

			<Stack spacing="sm">
				{supportedMethods?.includes('google') && (
					<AuthButton onClick={handleGoogle}>
						<Group spacing="2xs" align="center" position="center">
							<GoogleIcon />
							<Box>{buttonPrefix} with Google</Box>
						</Group>
					</AuthButton>
				)}
				{supportedMethods?.includes('microsoft') && (
					<AuthButton onClick={handleMicrosoft}>
						<Group spacing="2xs" align="center" position="center">
							<MicrosoftIcon />
							<Box>{buttonPrefix} with Microsoft</Box>
						</Group>
					</AuthButton>
				)}
				{supportedMethods?.includes('saml') && (
					<AuthButton onClick={handleSaml}>
						<Group spacing="2xs" align="center" position="center">
							<Icon name="key" />
							<Box>{buttonPrefix} with SAML SSO</Box>
						</Group>
					</AuthButton>
				)}
			</Stack>
		</Stack>
	);
}
